import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useApiSWR } from "@/hooks/useApiSWR";
import {
	getTaskCalendarEvent,
	createTaskCalendarEvent,
	startCalendarConnect,
	GCAL_NOT_CONNECTED,
	type TaskCalendarEvent,
} from "@/services/google-calendar.service";

// Loads the current user's calendar event for a task and exposes a create flow.
// On a "not connected" response it kicks off the Google consent redirect; the
// user returns to /tasks and re-opens the task to retry.
export function useTaskCalendarEvent(
	projectId: string | undefined,
	taskId: string | undefined,
) {
	const { data, error, mutate } = useApiSWR<TaskCalendarEvent | null>(
		projectId && taskId ? ["tasks", taskId, "calendar-event"] : null,
		() => getTaskCalendarEvent(projectId!, taskId!),
	);
	// Fetch errors read as "no event", matching the previous behavior.
	const event = data ?? null;
	// data can legitimately be null, so only undefined means still loading.
	const loading = data === undefined && !error && !!projectId && !!taskId;
	const [creating, setCreating] = useState(false);

	const create = useCallback(
		async (
			start: string,
			durationMin: number,
			title: string,
		): Promise<boolean> => {
			if (!projectId || !taskId) return false;
			setCreating(true);
			try {
				const created = await createTaskCalendarEvent(
					projectId,
					taskId,
					{
						start,
						durationMin,
						title,
					},
				);
				await mutate(created, { revalidate: false });
				toast.success("Added to Google Calendar");
				return true;
			} catch (e) {
				const err = e as { code?: string; message?: string };
				if (err.code === GCAL_NOT_CONNECTED) {
					try {
						const url = await startCalendarConnect();
						window.location.href = url;
					} catch {
						toast.error("Could not start Google connection");
					}
					return false;
				}
				toast.error("Failed to add to calendar", {
					description: err.message || "Please try again.",
				});
				return false;
			} finally {
				setCreating(false);
			}
		},
		[projectId, taskId, mutate],
	);

	return { event, loading, creating, create };
}
